import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { toast } from "react-toastify";
import MantisDataTable from "../../../components/common/MantisDataTable";
import { fetchDailyAttendence } from "../../../api/travelActivityApi";

export default function DailyAttendance() {
  const [attendanceData, setAttendanceData] = useState([]);
  const [dateRanges, setDateRanges] = useState([]);
  const [filters, setFilters] = useState({
    startDate: "",
    endDate: "",
    quickRange: "",
    user_type: "Employee",
    emp_status: 'ACTIVE'
  });

  useEffect(() => {
    setDateRanges(generateMonthRanges(6));
  }, []);

  // ✅ API CALL (payload includes startDate & endDate only)
  const loadAttendance = async (payload) => {
    try {
      const res = await fetchDailyAttendence(payload);
      if (res && Array.isArray(res)) {
        const flattened = res.map((item) => ({
          ...item,
          daily_attendance: item.daily_attendance || {},
        }));
        setAttendanceData(flattened);
      } else {
        setAttendanceData([]);
        toast.info("No records found");
      }
    } catch (err) {
      console.error("Error fetching daily attendance:", err);
      toast.error("Failed to load attendance data");
    }
  };

  // ✅ Handle Filter Change
  const handleFilterChange = (form) => {
    setFilters((prev) => ({ ...prev, ...form }));
  };

  // ✅ Handle Filter Submit
  const handleFilterSubmit = () => {
    let { startDate, endDate, quickRange, user_type,emp_status  } = filters;

    if ((!startDate || !endDate) && !quickRange) {
      toast.error("Please select a date range");
      return;
    }

    if (quickRange) [startDate, endDate] = quickRange.split(",");

    // ✅ Build final payload (exclude quickRange)
    const payload = { startDate, endDate, user_type,emp_status };

    loadAttendance(payload);
  };

  // ✅ Export to CSV
  const handleExport = () => {
    if (!attendanceData.length) {
      toast.warn("No data to export");
      return;
    }

    const sample = attendanceData[0];
    const dateKeys = Object.keys(sample.daily_attendance || {});
    const baseKeys = [
      "employee_name",
      "emp_code",
      "state",
      "reporting to",
      "total_days",
      "total_working_days",
      "total_half_days",
      "total_absent",
    ];
    const headers = [...baseKeys, ...dateKeys];
    const csvRows = [headers.join(",")];

    attendanceData.forEach((row) => {
      const values = baseKeys.map((key) => row[key] ?? "");
      const dateValues = dateKeys.map((d) => row.daily_attendance?.[d] ?? "");
      csvRows.push([...values, ...dateValues].join(","));
    });

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "DailyAttendance.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // ✅ Dynamic Date Columns with Color Coding
  const dateColumns =
    attendanceData.length > 0
      ? Object.keys(attendanceData[0].daily_attendance || {}).map((d) => ({
          label: d,
          key: d,
          type: "badge",
          getColor: (value) => {
            switch (value) {
              case "P":
                return "success"; // green
              case "L":
                return "info"; // sky blue
              case "HD":
                return "secondary"; // violet
              case "H":
                return "warning"; // yellow
              default:
                return "error"; // red for absent/other
            }
          },
        }))
      : [];

  const columns = [
    { label: "Employee Name", key: "employee_name", type: "text" },
    { label: "Employee Code", key: "emp_code", type: "text" },
    { label: "State", key: "state", type: "text" },
    { label: "Reporting To", key: "reporting to", type: "text" },
    { label: "Total Days", key: "total_days", type: "text" },
    { label: "Working Days", key: "total_working_days", type: "text" },
    { label: "Half Days", key: "total_half_days", type: "text" },
    { label: "Absent", key: "total_absent", type: "text" },
    ...dateColumns,
  ];

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1500px",
        overflowX: "auto",
        padding: 0,
      }}
    >
      <MantisDataTable
        title="Daily Attendance"
        data={attendanceData.map((item) => ({
          ...item,
          ...item.daily_attendance,
        }))}
        columns={columns}
        filters={[
          {
            label: "User Type",
            name: "user_type",
            type: "select",
            options: [
              { label: "Payroll Employee", value: "Employee" },
              { label: "Third-party Associates", value: "MDO" },
            ],
          },
          {
            label: "Date Range",
            name: "quickRange",
            type: "select",
            options: [{ label: "Select Date Range", value: "" }, ...dateRanges],
          },
          
  {
    label: "Emp Status",
    name: "emp_status",
    type: "select",
    options: [
      //{ label: "All", value: "ALL" },
      { label: "Active", value: "ACTIVE" },
      { label: "Inactive", value: "INACTIVE" },
    ],
  }
        ]}
        onFilterChange={handleFilterChange}
        onFilterSubmit={handleFilterSubmit}
        onExport={handleExport}
      />
    </Box>
  );
}

// ✅ Generate Month-wise Date Ranges (last N months)
function generateMonthRanges(monthsBack = 6) {
  const ranges = [];
  const today = new Date();
  for (let i = 0; i < monthsBack; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = d.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const start = `${year}-${String(month + 1).padStart(2, "0")}-01`;
    const end = `${year}-${String(month + 1).padStart(2, "0")}-${daysInMonth}`;
    ranges.push({
      label: `${start} - ${end}`,
      value: `${start},${end}`,
    });
  }
  return ranges;
}
